import * as THREE from 'three';
import { GLTFLoader, type GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { AssetId, ModelManifestEntry } from './types';

/**
 * The default set of models available out of the box. Real content will
 * grow this into the hundreds; `register()` also lets any other system
 * (e.g. a per-course content pack) add entries at runtime without editing
 * this file, which is the part that has to hold up at that scale.
 */
const DEFAULT_MODEL_MANIFEST: Record<AssetId, ModelManifestEntry> = {
  'tree.conifer.proxy': { url: '/assets/models/tree_proxy.gltf' },
  'bush.proxy': { url: '/assets/models/bush_proxy.gltf' },
  'grass.long.proxy': { url: '/assets/models/longgrass_proxy.gltf' },
};

export class ModelManager {
  private readonly loader = new GLTFLoader();
  private readonly manifest: Record<AssetId, ModelManifestEntry>;
  private readonly cache = new Map<AssetId, Promise<GLTF>>();

  constructor(manifest: Record<AssetId, ModelManifestEntry> = DEFAULT_MODEL_MANIFEST) {
    this.manifest = { ...manifest };
  }

  /** Adds or overrides a manifest entry at runtime (e.g. course-specific content packs). */
  register(id: AssetId, entry: ModelManifestEntry): void {
    this.manifest[id] = entry;
    this.cache.delete(id);
  }

  load(id: AssetId): Promise<GLTF> {
    const cached = this.cache.get(id);
    if (cached) return cached;

    const entry = this.manifest[id];
    if (!entry) {
      throw new Error(`ModelManager: unknown model id "${id}". Register it before loading.`);
    }

    const promise = this.loader.loadAsync(entry.url);
    this.cache.set(id, promise);
    return promise;
  }

  /** Returns a fresh clone of a cached GLTF's scene graph, safe to place multiple times. */
  async instantiate(id: AssetId): Promise<THREE.Object3D> {
    const gltf = await this.load(id);
    const clone = gltf.scene.clone(true);
    clone.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.castShadow = true;
        obj.receiveShadow = true;
      }
    });
    return clone;
  }
}
